/**
 * ServiceApp (serviceapp.ai) brand metadata + HTML rewriting.
 * Shared between the Vercel edge middleware and the client. The shared
 * index.html ships T&B branding; on SaaS hosts we rewrite title/meta/icons
 * at the edge and again client-side (for cached HTML).
 */
import {
  SERVICEAPP_SEO_META,
  SERVICEAPP_OG_IMAGE_PATH,
  HERO_LCP_IMAGE,
  HERO_LCP_IMAGE_MOBILE,
  resolveServiceAppSeoForPath,
  noscriptForServiceAppPath,
} from './serviceappSeo.js';

export const TB_BRAND = {
  siteName: 'Tools and Bubbles Cleaning Service Kochi',
  shortName: 'T&B',
  appleTitle: 'Tools & Bubbles',
  applicationName: 'Tools & Bubbles',
  themeColor: '#2563eb',
  icon: '/favicon.png',
  icon192: '/icon-192.png',
  icon512: '/icon-512.png',
  appleTouchIcon: '/apple-touch-icon.png',
  manifest: '/manifest.json',
};

export const SERVICEAPP_BRAND = {
  siteName: 'ServiceApp.ai',
  title: SERVICEAPP_SEO_META.title,
  description: SERVICEAPP_SEO_META.description,
  ogImage: SERVICEAPP_OG_IMAGE_PATH,
  themeColor: '#0f766e',
  /** Cache-bust so home-screen / favicon pick up the light-background mark. */
  icon: '/serviceapp-favicon.png?v=light-20260827',
  icon192: '/serviceapp-icon-192.png?v=light-20260827',
  icon512: '/serviceapp-icon-512.png?v=light-20260827',
  logo: '/serviceapp-logo.png',
  manifest: '/manifest.serviceapp.json',
};

/** Policy host for MTA-STS. It must serve only the well-known policy file. */
export const MTA_STS_POLICY_HOST = 'mta-sts.serviceapp.ai';

export function isMtaStsPolicyHost(host: string): boolean {
  const h = (host || '').toLowerCase().split(':')[0] ?? '';
  return h === MTA_STS_POLICY_HOST;
}

/** Hosts that belong to the SaaS platform (serviceapp.ai), not the T&B site. */
export function isSaasPlatformHost(host: string): boolean {
  const h = (host || '').toLowerCase().split(':')[0] ?? '';
  if (isMtaStsPolicyHost(h)) return false;
  return h === 'serviceapp.ai' || h.endsWith('.serviceapp.ai') || h.startsWith('serviceapp-platform');
}

/** Production Tools & Bubbles marketing + tenant site hosts. */
export function isToolsAndBubblesHost(host: string): boolean {
  const h = (host || '').toLowerCase();
  return h === 'toolsandbubbles.com' || h.endsWith('.toolsandbubbles.com');
}

/**
 * Custom-domain tenants get ServiceApp shell HTML — never the first-party T&B site.
 * toolsandbubbles.com resolves via public-org probe but must keep T&B PWA branding.
 */
export function isEligibleTenantCustomDomainHost(host: string): boolean {
  if (isSaasPlatformHost(host)) return false;
  if (isToolsAndBubblesHost(host)) return false;
  return true;
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/** Remove T&B-only signals that confuse Google (Kochi breadcrumb, local business, etc.). */
function stripToolsAndBubblesSeoSignals(html: string): string {
  let out = html;

  // External + inline JSON-LD (ld-website "Cleaning Service Kochi", ld-breadcrumb, LocalBusiness, …).
  out = out.replace(
    /\s*<script type="application\/ld\+json"[^>]*src="\/structured-data\/[^"]*"[^>]*><\/script>/gi,
    '',
  );
  out = out.replace(/\s*<script type="application\/ld\+json"[^>]*>[\s\S]*?<\/script>/gi, '');

  // Dublin Core + geo + T&B Open Graph locality (not rewritten by simple content= replace).
  out = out.replace(/\s*<meta name="DC\.[^"]*"[^>]*>/gi, '');
  out = out.replace(/\s*<meta name="geo\.[^"]*"[^>]*>/gi, '');
  out = out.replace(/\s*<meta name="ICBM"[^>]*>/gi, '');
  out = out.replace(
    /\s*<meta property="og:(street_address|locality|region|postal_code|country_name|locale:alternate)"[^>]*>/gi,
    '',
  );
  out = out.replace(/\s*<meta property="place:location:[^"]*"[^>]*>/gi, '');
  out = out.replace(/\s*<meta property="business:contact_data:[^"]*"[^>]*>/gi, '');

  // T&B static LCP shell text ("Cleaning Service Kochi") is still in DOM when only hidden via CSS.
  out = out.replace(/<div id="tb-static-shell"[\s\S]*?<\/main>\s*<\/div>/i, '');

  // Kochi SEO comment left in shared index.html head.
  out = out.replace(/\s*<!-- Primary SEO Meta Tags - Optimized to outrank[\s\S]*?-->\s*/i, '\n    ');

  return out;
}

/**
 * Rewrites the shared index.html for the ServiceApp domain: title, SEO meta,
 * OpenGraph/Twitter cards, icons, theme color and canonical, plus removes the
 * T&B static shell so no T&B branding flashes before React mounts.
 *
 * When `pathname` matches an industry SEO page, page-specific meta is injected
 * so crawlers indexing /cleaning-service-software etc. get the right title.
 */
export function rewriteHtmlForServiceApp(html: string, origin: string, pathname = '/'): string {
  const b = SERVICEAPP_BRAND;
  const base = origin.replace(/\/$/, '');
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  const seo = resolveServiceAppSeoForPath(normalizedPath);
  const title = escapeAttr(seo.title);
  const desc = escapeAttr(seo.description);
  const canonical = `${base}${seo.canonicalPath}`;
  const image = `${base}${seo.ogImagePath}`;
  const absIcon = `${base}${b.icon}`;
  const absIcon512 = `${base}${b.icon512}`;
  let out = stripToolsAndBubblesSeoSignals(html);

  // Never ship the T&amp;B cache-bust string on serviceapp.ai (crawlers treat it as the product version).
  out = out.replace(/\s*<meta name="build-version"[^>]*>/gi, '');

  out = out.replace(/<title>[\s\S]*?<\/title>/i, `<title>${title}</title>`);
  out = out.replace(/(<meta name="title" content=")[^"]*(")/i, `$1${title}$2`);
  out = out.replace(/(<meta name="description" content=")[^"]*(")/i, `$1${desc}$2`);
  out = out.replace(
    /(<meta name="copyright" content=")[^"]*(")/i,
    `$1\u00a9 ${new Date().getFullYear()} ServiceApp. All rights reserved.$2`,
  );
  out = out.replace(/(<meta name="author" content=")[^"]*(")/i, `$1ServiceApp$2`);

  out = out.replace(/(<meta property="og:title" content=")[^"]*(")/i, `$1${title}$2`);
  out = out.replace(/(<meta property="og:description" content=")[^"]*(")/i, `$1${desc}$2`);
  out = out.replace(/(<meta property="og:image" content=")[^"]*(")/i, `$1${image}$2`);
  out = out.replace(/(<meta property="og:site_name" content=")[^"]*(")/i, `$1${b.siteName}$2`);
  out = out.replace(/(<meta property="og:url" content=")[^"]*(")/i, `$1${canonical}$2`);
  out = out.replace(/(<meta property="og:locale" content=")[^"]*(")/i, `$1en_US$2`);

  out = out.replace(/(<meta property="twitter:title" content=")[^"]*(")/i, `$1${title}$2`);
  out = out.replace(/(<meta property="twitter:description" content=")[^"]*(")/i, `$1${desc}$2`);
  out = out.replace(/(<meta property="twitter:image" content=")[^"]*(")/i, `$1${image}$2`);
  out = out.replace(/(<meta property="twitter:url" content=")[^"]*(")/i, `$1${canonical}$2`);
  out = out.replace(/(<meta property="twitter:site" content=")[^"]*(")/i, `$1@ServiceAppAI$2`);
  out = out.replace(/(<meta property="twitter:creator" content=")[^"]*(")/i, `$1@ServiceAppAI$2`);

  // Icons: absolute URLs so Googlebot picks up favicon without executing JS.
  out = out.replace(/href="\/icon-192\.png"/g, `href="${absIcon512}"`);
  out = out.replace(/href="\/icon-512\.png"/g, `href="${absIcon512}"`);
  out = out.replace(/href="\/favicon\.png"/g, `href="${absIcon}"`);
  out = out.replace(/href="\/serviceapp-favicon\.png"/g, `href="${absIcon}"`);
  out = out.replace(/href="\/serviceapp-icon-512\.png"/g, `href="${absIcon512}"`);
  out = out.replace(/href="\/apple-touch-icon\.png"/g, `href="${absIcon512}"`);
  out = out.replace(/(<link rel="manifest" href=")[^"]*(")/i, `$1${b.manifest}$2`);

  out = out.replace(/(<meta name="apple-mobile-web-app-title" content=")[^"]*(")/i, `$1ServiceApp$2`);
  out = out.replace(/(<meta name="application-name" content=")[^"]*(")/i, `$1ServiceApp$2`);
  out = out.replace(/(<meta name="theme-color" content=")[^"]*(")/gi, `$1${b.themeColor}$2`);

  // Canonical to the SaaS origin (path-aware for industry pages).
  out = out.replace(/(<link rel="canonical" href=")[^"]*(")/i, `$1${canonical}$2`);

  // Google ignores meta keywords. Drop the long T&B list instead of rewriting it.
  out = out.replace(/\s*<meta name="keywords"[^>]*>/gi, '');

  // ServiceApp is global — point hreflang at the SaaS origin (geo tags removed above).
  out = out.replace(/(hreflang="en-IN" href=")[^"]*(")/i, `$1${base}/$2`);
  out = out.replace(/(hreflang="en" href=")[^"]*(")/i, `$1${base}/$2`);
  out = out.replace(/(hreflang="x-default" href=")[^"]*(")/i, `$1${base}/$2`);

  const noscriptHtml = noscriptForServiceAppPath(base, normalizedPath);
  out = out.replace(/<noscript>\s*<div style="max-width: 900px[\s\S]*?<\/noscript>/i, noscriptHtml);
  out = out.replace(/<noscript>\s*<div style="max-width:960px[\s\S]*?<\/noscript>/i, noscriptHtml);

  out = out.replace(/href="\/Logo1\.webp"/g, `href="${b.logo}"`);

  // Single ServiceApp JSON-LD graph (path-aware for industry pages).
  const ldJson = seo.structuredDataJson.replace(/<\//g, '<\\/');
  const lcpPreload =
    normalizedPath === '/' && HERO_LCP_IMAGE
      ? [
          HERO_LCP_IMAGE_MOBILE
            ? `<link rel="preload" as="image" href="${base}${HERO_LCP_IMAGE_MOBILE}" media="(max-width: 639px)" fetchpriority="high" />`
            : '',
          `<link rel="preload" as="image" href="${base}${HERO_LCP_IMAGE}" media="(min-width: 640px)" fetchpriority="high" />`,
        ]
          .filter(Boolean)
          .join('\n    ') + '\n    '
      : '';
  out = out.replace(
    '</head>',
    `${lcpPreload}<link rel="icon" href="${absIcon}" sizes="48x48" type="image/png" />
    <link rel="apple-touch-icon" href="${absIcon512}" />
    <script type="application/ld+json">${ldJson}</script></head>`,
  );

  return out;
}

/** Restore T&B manifest, icons and install name on toolsandbubbles.com (defensive client-side). */
export function applyTbBootDocument(): void {
  if (typeof document === 'undefined') return;
  const b = TB_BRAND;

  document.documentElement.classList.remove('saas-platform');
  document.documentElement.classList.remove('dark');
  document.documentElement.style.colorScheme = 'light';
  document.documentElement.style.setProperty('color-scheme', 'light only');
  document.documentElement.style.backgroundColor = '#ffffff';
  if (document.body) document.body.style.backgroundColor = '#ffffff';
  let colorSchemeMeta = document.querySelector('meta[name="color-scheme"]');
  if (!colorSchemeMeta) {
    colorSchemeMeta = document.createElement('meta');
    colorSchemeMeta.setAttribute('name', 'color-scheme');
    document.head.appendChild(colorSchemeMeta);
  }
  colorSchemeMeta.setAttribute('content', 'light only');

  const manifestLink = document.querySelector('link[rel="manifest"]');
  const currentIcon = document.querySelector('link[rel="icon"]')?.getAttribute('href') ?? '';
  const hadServiceAppManifest = manifestLink?.getAttribute('href')?.includes('serviceapp') ?? false;
  const needsIconReset = hadServiceAppManifest || currentIcon.includes('serviceapp') || !currentIcon;

  if (manifestLink) {
    manifestLink.setAttribute('href', b.manifest);
  } else {
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = b.manifest;
    document.head.appendChild(link);
  }

  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', b.themeColor);
  document.querySelector('meta[name="application-name"]')?.setAttribute('content', b.applicationName);
  document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute('content', b.appleTitle);

  if (needsIconReset) {
    document
      .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
      .forEach((el) => el.remove());

    const addIcon = (rel: string, href: string, sizes?: string) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.type = 'image/png';
      link.href = href;
      if (sizes) link.setAttribute('sizes', sizes);
      document.head.appendChild(link);
    };

    addIcon('icon', b.icon);
    addIcon('icon', b.icon192, '192x192');
    addIcon('icon', b.icon512, '512x512');
    addIcon('shortcut icon', b.icon);
    addIcon('apple-touch-icon', b.appleTouchIcon);
    addIcon('apple-touch-icon', b.appleTouchIcon, '180x180');
  }
}
